// A listing of files that comes a page at a time: a folder's, the top of the drive's, or Starred's.
// The first page is asked for when the listing is, and each one after it when "loadMore" is called,
// which the listing does as its end scrolls near. The cache adds each page onto those before it (see
// "typePolicies"), so what is handed back is every file scrolled to so far, until "hasMore" says
// there are no more.
//
// Listings are asked for again from the top whenever something changes them, by name, from all over
// the drive. So that doesn't fold a listing scrolled four pages deep back down to one, how many
// pages each listing has reached is kept here and asked for as its "first": asking again from the
// top then brings back as many files as are on screen. Reaching another page changes only "first",
// which the cache doesn't key on, so it reads back what is already there rather than asking again.
//
// While a search is answered, the listing it narrows stays on screen rather than giving way to the
// loader: its files are still the right place's, only more of them. A listing of another place has
// nothing to show until it comes.
import {useCallback, useLayoutEffect, useRef, useState} from 'react'
import {useQuery} from '@apollo/client/react'

export const PAGE_SIZE = 100

const NO_FILES = []

/**
 * "field" is the query's own field; "variables" name the place and any search, but not the page.
 */
const usePagedUploads = (query, {field, variables, skip = false, fetchPolicy = 'cache-first'}) => {
  const listing = JSON.stringify(variables)
  const placeKey = JSON.stringify({...variables, search: null})
  const [depths, setDepths] = useState({})
  const depth = depths[listing] || 1
  const {data, loading, fetchMore} = useQuery(query, {
    variables: {...variables, first: PAGE_SIZE * depth},
    skip,
    fetchPolicy,
    // Only the first ask of a visit goes by "fetchPolicy": reaching another page reads the cache. A
    // function, since Apollo goes back to "fetchPolicy" when the variables change for any other.
    nextFetchPolicy: () => 'cache-first'
  })
  const shown = useRef({placeKey: null, page: null})
  const asking = useRef(null)

  if (data?.[field]) shown.current = {placeKey, page: data[field]}

  const page = data?.[field] ?? (shown.current.placeKey === placeKey ? shown.current.page : null)

  const askForMore = useRef(null)

  // Before anything on screen can see the end and call for it.
  useLayoutEffect(() => {
    askForMore.current = () => {
      if (!page?.hasMore || asking.current === page.endCursor) return

      asking.current = page.endCursor
      fetchMore({variables: {after: page.endCursor, first: PAGE_SIZE}})
        .then(() => setDepths(current => ({...current, [listing]: (current[listing] || 1) + 1})))
        // Asked for again the next time the end comes near.
        .catch(() => { asking.current = null })
    }
  })

  // The same from one render to the next, so whatever watches for the end isn't set up afresh.
  const loadMore = useCallback(() => askForMore.current?.(), [])

  return {files: page?.nodes ?? NO_FILES, loaded: Boolean(page), loading, hasMore: Boolean(page?.hasMore), loadMore}
}

export default usePagedUploads
